import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import ConfirmActionSheet from "@/components/confirm-action-sheet"
import EndShiftSheet from "@/components/end-shift-sheet"
import { HandoverBar } from "@/components/handover-bar"
import Topbar from "@/components/topbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import { useFieldStatus, UseFormReturn } from "@/lib/form/form"
import { FormParamStatus } from "@/lib/form/types"
import { posthog } from "@/lib/posthog"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import { EnumsOf, useDebounceByHash } from "@/lib/utils"
import { DeptToggleGroup } from "@/packaging/components/dept-toggle-group"
import AddTruckProductsSheet from "@/rmpm/components/add-truck-products"
import { AddTruckSheet } from "@/rmpm/components/add-truck-sheet"
import NoTrucksAdded from "@/rmpm/components/no-trucks-added"
import RmPmForm from "@/rmpm/components/rmpm-form"
import {
  TruckTabs,
  TruckTabsContent,
  TruckTabsList,
  TruckTabsTrigger,
} from "@/rmpm/components/truck-tabs/truck-tabs"
import { useRmPmForm } from "@/rmpm/lib/form"
import { Schema, SchemaBuilder, schemaPathToValuePath } from "@/rmpm/lib/schema"
import { AutoSaveIndicator, AutoSaveStatus } from "@/rmpm/lib/auto-save-indicator"
import { handleNetworkError, handleOfflineActionError } from "@/services/base"
import {
  deleteRMPMProductSheet,
  discardRMPMProcess,
  endRMPMProcess,
  getProducts as getRMPMProducts,
  submitRMPMForm,
} from "@/services/rmpm"
import { handleApiError } from "@/services/error-handler"
import { getShiftStatus, setSession } from "@/services/session"
import useStore from "@/store"
import { selectPmForm, selectRmForm, TruckType } from "@/store/rmpm"
import { ShiftStatus } from "@/store/session"
import { LabelValue } from "@/types/common"
import { HTTPError } from "ky"
import { get, cloneDeep } from "lodash"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

const truckTypes: LabelValue[] = [
  {
    id: "rm",
    label: "RM",
    value: "rm",
  },
  {
    id: "pm",
    label: "PM",
    value: "pm",
  },
]

const RmPmSheets = {
  None: "None",
  EndShift: "EndShift",
  AddTruck: "AddTruck",
  AddProducts: "AddProducts",
  AddProductSku: "AddProductSku",
} as const

const Sheets = {
  EndShift: "EndShift",
  DiscardShift: "DiscardShift",
  EndShiftError: "EndShiftError",
  ConfirmSubmit: "ConfirmSubmit",
  DeleteLine: "DeleteLine",
  None: "None",
} as const

type RmPmSheets = EnumsOf<typeof RmPmSheets>

export default function Rmpm() {
  const debounce = useDebounceByHash(250)
  const navigate = useNavigate()
  const userStore = useStore.use.user()
  const sessionStore = useStore.use.session()
  const rmpmStore = useStore.use.rmpm()
  const [currSheet, setCurrSheet] = useState<RmPmSheets>(RmPmSheets.None)
  const isSyncing = useRef(false)
  const [sheetState, setSheetState] = useState("")
  const [handovers, setHandovers] = useState<Record<string, any>[]>([])
  const [currHandoverSessionId, setCurrHandoverSessionId] = useState("")

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle")
  const [autoSaveErrorCount, setAutoSaveErrorCount] = useState(0)

  const [truckType, setTruckType] = useState<TruckType>("rm")
  const [currTruckId, setCurrTruckId] = useState<string>("")

  const metaSchema = useMemo(() => {
    return rmpmStore.trucks[truckType]?.find(
      (truck: Schema) => truck.sheetId === currTruckId
    )
  }, [currTruckId, rmpmStore.trucks])

  const { form, rebuild: rebuildForm } = useRmPmForm()
  const formStatus = useFieldStatus({
    form,
  })

  const { schema, deleteConfirmOpen, confirmDelete, cancelDelete } = useSchemaAndForm()

  const truckNumber = useMemo(() => {
    if (!metaSchema) {
      return ""
    }

    return (
      (metaSchema.properties?.truckNumber.const as string | undefined) || ""
    )
  }, [metaSchema])

  const pmType = useMemo(() => {
    return (
      (metaSchema?.properties?.general.items?.at(0)?.properties?.value
        .default as string | undefined) || ""
    )
  }, [metaSchema])

  const { isLoading, error } = useEnsureDataLoaded()
  const [showLogoutSheet, setShowLogoutSheet] = useState(false)
  useInit()

  function useEnsureDataLoaded() {
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [retryCount, setRetryCount] = useState(0)
    const loadingRef = useRef(false)

    const loadData = async () => {
      // Check if data already loaded
      const hasProducts =
        rmpmStore.allProducts.pmProducts.length > 0 &&
        rmpmStore.allProducts.rmProducts.length > 0
      const hasProcessData = !!rmpmStore.process.processId

      if (hasProducts && hasProcessData) {
        return // All data present
      }

      // Prevent duplicate requests
      if (loadingRef.current) {
        return
      }

      loadingRef.current = true
      setIsLoading(true)
      setError(null)

      try {
        // Fetch products and session in parallel
        const [productsResult, sessionResult] = await Promise.allSettled([
          hasProducts ? Promise.resolve(null) : getRMPMProducts(),
          hasProcessData ? Promise.resolve(null) : getShiftStatus(),
        ])

        // Handle products - getRMPMProducts already updates the store
        if (productsResult.status === 'rejected') {
          throw new Error('Failed to load RM/PM products')
        }

        // Handle session
        if (sessionResult.status === 'fulfilled' && sessionResult.value) {
          setSession(sessionResult.value.shiftSession)

          // Find RM/PM process in session
          const rmpmProcess = sessionResult.value.shiftSession?.employeeSessions?.find(
            (proc) => proc.processName === "Raw Material and Packaging Material"
          )

          if (rmpmProcess) {
            rmpmStore.setProcessDetails({
              processId: rmpmProcess.processId,
              shiftId: sessionResult.value.shiftSession!.shiftId,
            })
          }
        } else if (sessionResult.status === 'rejected') {
          throw new Error('Failed to load session data')
        }

        // Reset retry count on success
        setRetryCount(0)

      } catch (err) {
        const newRetryCount = retryCount + 1
        setRetryCount(newRetryCount)

        // Log error with context
        await handleApiError(
          err,
          {
            operation: "load RM/PM data",
            context: {
              hasProducts,
              hasProcessData,
              retryAttempt: newRetryCount,
            },
            showToast: false, // Show custom toast with retry button
          },
          navigate
        )

        const errorMessage = err instanceof Error ? err.message : "Failed to load RM/PM data"
        setError(errorMessage)

        // Show error toast with retry button
        if (err instanceof Error && err.name !== "TypeError") {
          // TypeError is handled by handleNetworkError (redirects to /network-error)
          toast.error(errorMessage, {
            description: "Please check your connection and try again.",
            duration: 10000,
            action: {
              label: "Retry",
              onClick: () => {
                void loadData()
              },
            },
          })
        }
      } finally {
        setIsLoading(false)
        loadingRef.current = false
      }
    }

    useEffect(() => {
      void loadData()
    }, [])

    return { isLoading, error, retryCount }
  }

  function isSubmitted(schemaId: string | undefined): boolean {
    return rmpmStore.submitted[schemaId || ""] === FormParamStatus.SUBMITTED
  }

  function hasRejectIntent(value: any): boolean {
    if (Array.isArray(value)) {
      return value.some((item) => hasRejectIntent(item))
    }

    if (!value || typeof value !== "object") {
      return false
    }

    if (value.__rejectRequested === true) {
      return true
    }

    return Object.values(value).some((child) => hasRejectIntent(child))
  }

  function mergeIntoActiveSchemaShape(baseValue: any, sourceValue: any): any {
    if (Array.isArray(baseValue)) {
      if (!Array.isArray(sourceValue)) {
        return cloneDeep(baseValue)
      }

      return baseValue.map((item, index) =>
        mergeIntoActiveSchemaShape(item, sourceValue[index])
      )
    }

    if (baseValue && typeof baseValue === "object") {
      if (!sourceValue || typeof sourceValue !== "object") {
        return cloneDeep(baseValue)
      }

      const mergedValue: Record<string, any> = cloneDeep(baseValue)

      for (const [key, value] of Object.entries(sourceValue)) {
        if (key in mergedValue) {
          mergedValue[key] = mergeIntoActiveSchemaShape(mergedValue[key], value)
        } else {
          mergedValue[key] = cloneDeep(value)
        }
      }

      return mergedValue
    }

    return sourceValue === undefined ? cloneDeep(baseValue) : cloneDeep(sourceValue)
  }

  function findMissingRequiredFields(data: { general: any; products: any }) {
    const missing: any[] = [];

    function check(item: any, path: string) {
      const isMissingValue =
        item &&
        typeof item === "object" &&
        item.id &&
        !(Array.isArray(item.parameters) && item.parameters.length > 0) &&
        (item.value === "" || item.value === null || item.value === undefined)

      if (currHandoverSessionId) {
        if (isMissingValue) missing.push(path);
      } else {
        if (
          item &&
          typeof item === "object" &&
          "required" in item &&
          item.required === true &&
          !(Array.isArray(item.parameters) && item.parameters.length > 0) &&
          isMissingValue
        ) {
          missing.push(path);
        }
      }

      if (Array.isArray(item)) {
        item.forEach((v, i) => check(v, `${path}[${i}]`));
        return;
      }

      if (item && typeof item === "object") {
        for (const key in item) {
          if (!item.hasOwnProperty(key)) continue;

          if (!isNaN(Number(key))) {
            check(item[Number(key)], `${path}[${key}]`);
          }

          // Handle nested structures
          if (key === "parameters" && Array.isArray(item.parameters)) {
            item.parameters.forEach((v: any, i: any) => check(v, `${path}.parameters[${i}]`));
          }

          if (key === "skus" && typeof item.skus === "object") {
            for (const sKey in item.skus) {
              if (!isNaN(Number(sKey))) {
                check(item.skus[Number(sKey)], `${path}.skus[${sKey}]`);

                if (Array.isArray(item.skus[Number(sKey)].parameters)) {
                  item.skus[Number(sKey)].parameters.forEach((v: any, i: any) =>
                    check(v, `${path}.skus[${sKey}].parameters[${i}]`)
                  );
                }
              }
            }
          }
        }
      }
    }

    if (data.general) check(data.general, "general");
    if (data.products) check(data.products, "products");

    return missing;
  }

  function isSubmitDisabled(): boolean {
    if (!currTrucks().length) {
      return true
    }

    if (isSubmitted(schema.sheetId || "")) {
      return true
    }

    if (hasRejectIntent(form.getValues()?.products)) {
      return false
    }

    if (formStatus !== FormParamStatus.VALID) {
      return true
    }

    const missingFields = findMissingRequiredFields(form.getValues())
    return missingFields.length > 0
  }

  function useInit() {
    useLayoutEffect(() => {
      setHandovers(rmpmStore.handovers ?? [])

      const newTruckType = truckTypes.at(0)?.id as TruckType
      if (!newTruckType) {
        return
      }
      setTruckType(newTruckType)

      let newTruckId = "" as string;

      if (currHandoverSessionId) {
        newTruckId = currTrucks().at(0)?.sheetId || ""
      } else {
        newTruckId = rmpmStore.trucks[newTruckType].filter((i) => !i.isHandover).at(0)?.sheetId || ("" as string)
      }

      setCurrTruckId(newTruckId)
    }, [])

    useEffect(() => {
      if (!currTruckId) {
        setCurrTruckId(currTrucks().at(0)?.sheetId || "")
      }
    }, [rmpmStore.trucks, truckType])
  }

  function useSchemaAndForm() {
    const [schema, setSchema] = useState<Schema>({})
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<{ schema: Schema | undefined; path: string } | null>(null)

    function syncFormToStore() {
      const values = form.getValues()
      sessionStore.setProcessUpdatedAt(
        rmpmStore.process.processId || "",
        new Date().toISOString()
      )
      if (truckType === "rm") {
        rmpmStore.setRmForm(values)
      } else {
        rmpmStore.setPmForm(values)
      }
    }

    async function syncToApi() {
      try {
        if (
          !schema ||
          !metaSchema ||
          isSubmitted(schema.sheetId) ||
          isSyncing.current || currHandoverSessionId
        ) {
          return
        }

        isSyncing.current = true
        setAutoSaveStatus("saving")

        await submitRMPMForm({
          schema,
          truckType: truckType,
          formData: form.getValues(),
          status: FormParamStatus.DRAFT,
        })

        isSyncing.current = false
        setAutoSaveStatus("saved")
        setAutoSaveErrorCount(0)

        // Auto-hide "saved" status after 2 seconds
        setTimeout(() => {
          setAutoSaveStatus("idle")
        }, 2000)
      } catch (err) {
        isSyncing.current = false
        setAutoSaveStatus("error")
        const newErrorCount = autoSaveErrorCount + 1
        setAutoSaveErrorCount(newErrorCount)

        // Log error to PostHog with context
        await handleApiError(
          err,
          {
            operation: "auto-save draft",
            context: {
              truckNumber: metaSchema?.properties?.truckNumber?.const as string,
              sheetId: schema?.sheetId,
              consecutiveFailures: newErrorCount,
            },
            silent: true, // Don't show toast for every failure
          },
          navigate
        )

        // Show prominent warning after 3 consecutive failures
        if (newErrorCount >= 3) {
          toast.error(
            "Auto-save has failed multiple times. Your changes may not be saved.",
            {
              description: "Please check your connection and try submitting manually.",
              duration: 10000,
            }
          )
        }
      }
    }

    function refineSchema(metaSchema: Schema, values?: Record<string, any>) {
      const builder = new SchemaBuilder(metaSchema)
      builder.refineMetaSchema(values)
      setSchema(builder.schema)

      return builder.schema
    }

    async function handleChangeMetaSchema() {
      if (metaSchema) {

        let storeFormData: Record<string, any> | undefined

        if (truckType === "rm") {
          storeFormData = selectRmForm(metaSchema.sheetId || "")(rmpmStore)
        } else {
          storeFormData = selectPmForm(metaSchema.sheetId || "")(rmpmStore)
        }

        const refined = refineSchema(metaSchema, storeFormData)
        await rebuildForm(refined)
        const defaultFormData = form.getValues()
        const shouldForcePreview = isSubmitted(
          refined.sheetId || metaSchema.sheetId || ""
        )

        if (storeFormData) {
          // Preserve schema-registered defaults for missing fields while applying hydrated/store values.
          const mergedFormData = mergeIntoActiveSchemaShape(
            defaultFormData,
            storeFormData
          )
          form.setValue("", mergedFormData)
        } else {
          form.setValue("", defaultFormData)
        }

        if (shouldForcePreview) {
          await setFieldsToSubmitted(form)
        }

        syncFormToStore()

      } else {
        setSchema({})
      }
    }

    useEffect(() => {
      void handleChangeMetaSchema()
    }, [metaSchema])

    // Helper to check if path is for a SKU (contains 'skus.items')
    function isSkuPath(path: string): boolean {
      return path.includes('skus.items')
    }

    /**
     * Extracts the SKU parent path from a full schema path
     * 
     * Expected path format: "properties.products.items[N].properties.skus.items[M]"
     * where N is the product index and M is the SKU index
     * 
     * @param path - Full path like "properties.products.items[0].properties.skus.items[1]"
     * @returns Parent path like "properties.products.items[0].properties.skus" or empty string on error
     * 
     * @note This function uses regex-based string parsing which is fragile if the schema
     * structure changes. Consider using a path parsing library or type-safe path construction
     * if the schema format becomes more dynamic.
     */
    function getSkuPath(path: string): string {
      try {
        // Path looks like: properties.products.items[0].properties.skus.items[1]
        // We need to extract: properties.products.items[0].properties.skus
        if (!path || typeof path !== 'string') {
          console.error('[getSkuPath] Invalid path type:', typeof path)
          return ''
        }
        
        // Validate path format: must contain .skus.items[n] pattern
        if (!path.includes('.skus.items[')) {
          console.error('[getSkuPath] Path does not contain .skus.items[]:', path)
          return ''
        }
        
        const match = path.match(/(.+\.skus)\.items\[(\d+)\]$/)
        if (!match || match.length < 3) {
          console.error('[getSkuPath] Failed to match SKU path pattern:', path)
          return ''
        }
        
        // Validate that the extracted index is a valid number
        const index = parseInt(match[2], 10)
        if (isNaN(index) || index < 0) {
          console.error('[getSkuPath] Invalid SKU index:', match[2])
          return ''
        }
        
        return match[1]
      } catch (error) {
        console.error('[getSkuPath] Unexpected error parsing path:', error)
        return ''
      }
    }

    /**
     * Confirms and executes the SKU deletion
     */
    async function confirmDelete() {
      if (!itemToDelete?.schema?.id) {
        console.error('[confirmDelete] No item to delete')
        return
      }
      
      const skuPath = getSkuPath(itemToDelete.path)
      if (!skuPath) {
        console.error('[confirmDelete] Failed to extract SKU path from:', itemToDelete.path)
        toast.error("Failed to delete SKU: The item structure has changed. Please refresh and try again.")
        return
      }

      // Remove SKU from store
      rmpmStore.removeSkuFromProduct(truckType, currTruckId, skuPath, itemToDelete.schema.id)
      toast.success("SKU deleted successfully")
      
      // Close dialog and clear state
      setDeleteConfirmOpen(false)
      setItemToDelete(null)
    }
    
    /**
     * Cancels the delete operation and clears the item to delete
     */
    function cancelDelete() {
      setDeleteConfirmOpen(false)
      setItemToDelete(null)
    }

    useEffect(() => {
      async function handleDelete(schema: Schema | undefined, path: string) {
        if (!schema?.id) {
          toast.error("Item id missing in schema")
          return
        }

        // Check if we're deleting a SKU
        if (isSkuPath(path)) {
          // Handle SKU deletion (client-side only) - show confirmation first
          const skuPath = getSkuPath(path)
          if (!skuPath) {
            toast.error("Cannot delete SKU: invalid path")
            return
          }

          // Open confirmation dialog
          setItemToDelete({ schema, path })
          setDeleteConfirmOpen(true)
          return
        }

        // Handle Product deletion (requires API call)
        // Find the truck and get server-generated IDs
        const trucks = rmpmStore.trucks[truckType]
        const truckIdx = trucks.findIndex((t: Schema) => t.sheetId === currTruckId)
        const truck = trucks.at(truckIdx)
        const sheetId = truck?.sheetId
        const productSheetId = schema?.productSheetId

        if (!sheetId || !productSheetId) {
          toast.error("Cannot delete product: missing server IDs")
          return
        }

        // Store state for rollback
        const currentTruckState = cloneDeep(truck)
        const currentFormState = truckType === "rm"
          ? cloneDeep(selectRmForm(currTruckId)(rmpmStore))
          : cloneDeep(selectPmForm(currTruckId)(rmpmStore))
        const previousTruckId = currTruckId

        // Optimistically update UI - remove product immediately
        if (truck?.properties?.products.items) {
          if (truck?.properties?.products.items.length < 2) {
            if (truckIdx === 0) {
              setCurrTruckId(trucks.at(truckIdx + 1)?.sheetId || "")
            } else {
              setCurrTruckId(trucks.at(truckIdx - 1)?.sheetId || "")
            }
          }
        }

        if (truckType === "rm") {
          rmpmStore.removeRmProduct(currTruckId, schema.id)
        } else if (truckType === "pm") {
          rmpmStore.removePmProduct(currTruckId, schema.id)
        }

        // Show loading toast
        const deletingToast = toast.loading("Deleting product...")

        try {
          // Call delete API
          await deleteRMPMProductSheet(sheetId, productSheetId)

          // Success - update toast
          toast.success("Product deleted successfully", { id: deletingToast })
        } catch (err) {
          // Rollback on failure - restore state
          if (currentTruckState) {
            if (truckType === "rm") {
              rmpmStore.addRmTrucksMetaSchemas([currentTruckState])
              if (currentFormState) {
                rmpmStore.setRmForm(currentFormState)
              }
            } else {
              rmpmStore.addPmTrucksMetaSchemas([currentTruckState])
              if (currentFormState) {
                rmpmStore.setPmForm(currentFormState)
              }
            }
            setCurrTruckId(previousTruckId)
          }

          // Update toast to error
          toast.error("Failed to delete product", { id: deletingToast })

          // Log error with context
          await handleApiError(
            err,
            {
              operation: "delete product",
              context: {
                sheetId,
                productSheetId,
                truckNumber: truck?.properties?.truckNumber?.const as string,
              },
              showToast: false, // Already showing toast
            },
            navigate
          )
        }
      }

      form.addEventHandler("change", () => {
        debounce("syncFormToStore", syncFormToStore)
        debounce("draftSubmit", syncToApi, 2000)
        debounce("refineSchema", async () => {
          if (metaSchema) {
            const refined = refineSchema(metaSchema, form.getValues())
            const values = form.getValues()
            rebuildForm(refined)
            const nextValues = mergeIntoActiveSchemaShape(form.getValues(), values)
            form.setValue("", nextValues, { shouldTrigger: false })
          }
        })
      })

      form.addEventHandler("clickAdd", async (__e, ctx) => {
        if (!ctx.schema) {
          return
        }

        if (ctx.path.endsWith("products")) {
          setCurrSheet(RmPmSheets.AddProducts)
        } else if (ctx.path.endsWith("skus")) {
          setCurrSheet(RmPmSheets.AddProductSku)
          const eventSchema = ctx.schema as Schema
          const referenceSchema = get(
            metaSchema,
            eventSchema.options?.addFrom?.path || ""
          )

          // Extract product information from the reference schema
          const productId = referenceSchema?.id || ""
          const productName = referenceSchema?.options?.label || "Unknown Product"
          
          // Get SKU name from the SKU choices in the schema
          const skuChoices = referenceSchema?.properties?.parameters?.items?.[1]?.options?.choices
          const defaultSkuName = skuChoices?.[0]?.label || "SKU"
          
          // Get current market value from form data
          const formValues = form.getValues()
          const productPath = eventSchema.options?.addFrom?.path || ""
          const productData = productPath ? get(formValues, schemaPathToValuePath(productPath)) : null
          const currentMarket = productData?.parameters?.[0]?.value || "DOMESTIC"

          try {
            await rmpmStore.addSkuToProduct(
              truckType,
              currTruckId,
              ctx.path,
              referenceSchema,
              {
                productId,
                productName,
                skuName: defaultSkuName,
                market: currentMarket === "Export" ? "EXPORT" : "DOMESTIC",
              }
            )
          } catch (error) {
            console.error("Failed to add SKU:", error)
            toast.error(error instanceof Error ? error.message : "Failed to add SKU")
          }
        } else {
          setCurrSheet(RmPmSheets.None)
        }
      })

      form.addEventHandler("clickDelete", async (__e, ctx) => {
        await handleDelete(ctx.schema as Schema | undefined, ctx.path)
      })

      return () => {
        form.removeEventHandler("change")
        form.removeEventHandler("clickAdd")
        form.removeEventHandler("clickDelete")
      }
    }, [form, metaSchema, currTruckId, schema, rmpmStore, truckType])

    return {
      schema,
      deleteConfirmOpen,
      confirmDelete,
      cancelDelete,
    }
  }

  function canEdit(): boolean {

    if (currHandoverSessionId) {
      return false
    }

    if (!rmpmStore.process.processId) {
      return true
    }

    const process = sessionStore.session.processes.find(
      (proc: { processId: string }) => proc.processId === rmpmStore.process.processId
    )
    if (!process) {
      return false
    }

    return (
      process.status === ShiftStatus.Started ||
      process.status === ShiftStatus.Draft
    )
  }

  function currTrucks() {
    if (currHandoverSessionId) {
      const result = handovers
        .filter(handover => handover.handoverFromEmployeeSessionId === currHandoverSessionId)
        .flatMap(handover =>
          handover.trucks[truckType]
            .map((truck: { truckNumber: string; sheetId: string }) => ({
              truckNumber: truck.truckNumber,
              sheetId: truck.sheetId,
            }))
        );
      return result
    }
    if (truckType === "rm") {
      return rmpmStore.trucks.rm.filter((truck: Schema) => !truck.isHandover)
    }

    return rmpmStore.trucks.pm.filter((truck: Schema) => !truck.isHandover)
  }

  async function handleClickBack() {
    void getShiftStatus().then((res) => {
      if (res.shiftSession) {
        setSession(res.shiftSession)
      }
    })

    await navigate("/manage-processes")
  }

  async function handleDiscardProcess() {
    try {
      await discardRMPMProcess()
      await navigate("/manage-processes")
    } catch (err) {
      posthog.captureException(err)

      if (
        handleOfflineActionError(err, { action: "discard", target: "process" })
      ) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      toast.error("Error discarding shift")
    }
  }

  async function handleSubmit() {
    try {
      if (!schema || !metaSchema) {
        toast.error("No active schema found")

        return
      }

      isSyncing.current = true

      await submitRMPMForm({
        schema,
        truckType: truckType,
        formData: form.getValues(),
        status: FormParamStatus.SUBMITTED,
      })
      rmpmStore.addToSubmitted(schema.sheetId || "")

      await setFieldsToSubmitted(form)
      isSyncing.current = false
    } catch (err) {
      isSyncing.current = false

      console.error(err)
      posthog.captureException(err)

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      if (err instanceof Error) {
        toast.error(err.message)

        return
      }

      toast.error("Something went wrong while submitting")
    } finally {
      handleSheetChange(Sheets.ConfirmSubmit, false);
    }
  }

  async function setFieldsToSubmitted(form: UseFormReturn) {
    function markStatusSubmitted(value: any, insideRejectedProduct = false): any {
      if (Array.isArray(value)) {
        return value.map((item) => markStatusSubmitted(item, insideRejectedProduct))
      }

      if (value && typeof value === "object") {
        const hasRejectIntent = value.__rejectRequested === true
        const nextInsideRejected = insideRejectedProduct || hasRejectIntent
        const nextValue: Record<string, any> = {}
        for (const [key, child] of Object.entries(value)) {
          nextValue[key] = markStatusSubmitted(child, nextInsideRejected)
        }

        if ("status" in nextValue) {
          const currentStatus = nextValue.status

          if (hasRejectIntent) {
            nextValue.status = FormParamStatus.REJECTED_APPROVED
            return nextValue
          }

          if (insideRejectedProduct) {
            if (
              currentStatus !== FormParamStatus.REJECTED_PENDING_APPROVAL &&
              currentStatus !== FormParamStatus.REJECTED_APPROVED
            ) {
              nextValue.status = FormParamStatus.REJECTED_APPROVED
            }
            return nextValue
          }

          if (
            currentStatus !== FormParamStatus.NOT_APPLICABLE &&
            currentStatus !== FormParamStatus.REJECTED_PENDING_APPROVAL &&
            currentStatus !== FormParamStatus.REJECTED_APPROVED
          ) {
            nextValue.status = FormParamStatus.SUBMITTED
          }
        }

        return nextValue
      }

      return value
    }

    const values = markStatusSubmitted(form.getValues())
    form.setValue("", values, { shouldTrigger: false })
  }

  async function handleClickEndProcess() {
    setCurrSheet(RmPmSheets.EndShift)
  }

  async function handleEndProcess() {
    try {
      await endRMPMProcess()
      await navigate("/shift-ended")
    } catch (err) {
      console.error(err)
      posthog.captureException(err)

      if (handleOfflineActionError(err, { action: "end", target: "process" })) {
        return
      }

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)

        return
      }

      if (err instanceof Error) {
        toast.error(err.message)

        return
      }

      toast.error("Something went wrong while ending process")
    }
  }

  function handleChangeTruckType(newTruckType: TruckType) {
    if (newTruckType) {
      setTruckType(newTruckType)

      let newTruckId = "" as string;

      if (currHandoverSessionId) {
        const result = handovers
          .filter(handover => handover.handoverFromEmployeeSessionId === currHandoverSessionId)
          .flatMap(handover =>
            handover.trucks[truckType].map((product: { truckNumber: string; sheetId: string }) => ({
              truckNumber: product.truckNumber,
              sheetId: product.sheetId,
            }))
          );
        newTruckId = result.at(0)?.sheetId || ""
      } else {
        newTruckId = rmpmStore.trucks[newTruckType].filter(i => !i.isHandover).at(0)?.sheetId || ("" as string)
      }
      setCurrTruckId(newTruckId)
    }
  }

  function handleSheetChange(sheet: string, open: boolean) {
    if (open) {
      setSheetState(sheet)
    } else {
      setSheetState("")
    }
  }

  function closeSheets() {
    setCurrSheet(RmPmSheets.None)
  }

  function handleClickAddTruck() {
    if (currHandoverSessionId) return;
    setCurrSheet(RmPmSheets.AddTruck)
  }

  function handleSwitchToHandover(
    checked: boolean,
    __sessionId: string
  ) {
    if (checked) {
      setCurrHandoverSessionId(__sessionId)
    } else {
      setCurrHandoverSessionId("")
    }
  }

  useEffect(() => {
    setCurrTruckId(currTrucks().at(0)?.sheetId || "")
  }, [currHandoverSessionId])

  function getIndicatorColor(sheetId: string): string {
    return isSubmitted(sheetId) ? "#2b974fff" : "#FAA226CC"
  }

  // Loading State - Full Page Spinner
  if (isLoading) {
    return (
      <div className="bg-backdrop h-screen overflow-auto">
        <Topbar
          className="sticky top-0 z-10"
          title="RM/PM"
          subtitle={userStore.plant.name}
          showBack
          onClickBack={handleClickBack}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <div className="text-label-text text-lg">Loading RM/PM data...</div>
          </div>
        </div>
      </div>
    )
  }

  // Error State - Error Message with Go Back Button
  if (error) {
    return (
      <div className="bg-backdrop h-screen overflow-auto">
        <Topbar
          className="sticky top-0 z-10"
          title="RM/PM"
          subtitle={userStore.plant.name}
          showBack
          onClickBack={handleClickBack}
        />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center flex flex-col gap-4 max-w-md px-4">
            <div className="text-error text-lg font-medium">{error}</div>
            <button
              onClick={() => navigate("/manage-processes")}
              className="px-4 py-2 bg-button-primary text-white rounded"
            >
              Go Back to Processes
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Normal State - Original UI
  return (
    <div className="bg-backdrop h-screen overflow-auto">
      <Topbar
        className="sticky top-0 z-10"
        title="RM/PM"
        subtitle={userStore.plant.name}
        showBack
        onClickBack={handleClickBack}
      >
        <div className="flex items-center gap-5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Icon name="MoreVertical" className="text-button-black h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent inPortal>
              {canEdit() && (
                <>
                  <DropdownMenuItem onClick={handleClickEndProcess}>
                    End Process
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDiscardProcess}>
                    Discard and Exit
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Topbar>

      {!!handovers.length && (
        <div>
          {handovers.map((handover) => (
            <HandoverBar
              key={handover.handoverFromEmployeeSessionId}
              employeeCode={handover.handoverFromEmployeeCode}
              sessionId={handover.handoverFromEmployeeSessionId}
              checked={
                currHandoverSessionId === handover.handoverFromEmployeeSessionId
              }
              onCheckedChange={(checked, sessionId) =>
                handleSwitchToHandover(checked, sessionId)
              }
            />
          ))}
        </div>
      )}

      <div className="py-4 min-h-screen">
        <div className="px-4">
          {/* Auto-save indicator */}
          {currTrucks().length > 0 && !currHandoverSessionId && (
            <div className="flex justify-center mb-2">
              <AutoSaveIndicator
                status={autoSaveStatus}
                errorCount={autoSaveErrorCount}
              />
            </div>
          )}

          <DeptToggleGroup
            className="mb-6 mx-auto"
            type="single"
            options={truckTypes}
            defaultValue={truckTypes.at(0)?.value}
            value={truckType}
            onValueChange={handleChangeTruckType}
          />
        </div>

        {currTrucks().length ? (
          <TruckTabs value={currTruckId} onValueChange={setCurrTruckId}>
            <div className="px-4">
              <TruckTabsList
                canAdd={canEdit()}
                onClickAdd={handleClickAddTruck}
              >
                {currTrucks().map((truck: { sheetId: string; truckNumber: string; options: { label: string } }) => (
                  <TruckTabsTrigger key={truck.sheetId} value={truck.sheetId || ""}>
                    <div className="flex flex-col items-start font-medium">
                      {/* Top row: TRUCK + indicator dot */}
                      <div className="flex flex-row items-center justify-between gap-2 text-tiny w-full">
                        <span>TRUCK</span>
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: getIndicatorColor(truck.sheetId) }}
                        />
                      </div>

                      {/* Truck number */}
                      <div className="text-sm">{truck.truckNumber || truck.options?.label}</div>
                    </div>
                  </TruckTabsTrigger>
                ))}
              </TruckTabsList>
            </div>

            {currTrucks().map((truck: { sheetId: string }) => (
              <TruckTabsContent key={truck.sheetId} value={truck.sheetId || ""}>
                {schema && schema.isResample && <div className="p-3 pr-3.5 flex gap-6 items-center justify-between bg-resample text-button-black">
                  <span className="font-medium text-sm">This is a resample case</span>
                </div>
                }
                <RmPmForm
                  className="mt-4"
                  schema={schema}
                  form={form}
                  editable={currHandoverSessionId ? false : !isSubmitted(schema.sheetId)}
                />
              </TruckTabsContent>
            ))}
          </TruckTabs>
        ) : (
          <NoTrucksAdded
            className="mt-30 px-8"
            truckType={truckType}
            onClickAdd={handleClickAddTruck}
            isHandover={!!currHandoverSessionId}
          />
        )}
      </div>

      {!!currTrucks().length && (
        <Bottombar className="flex sticky bottom-0 left-0">
          <Button
            className="grow"
            color={isSubmitted(schema.sheetId) ? "success" : undefined}
            disabled={isSubmitDisabled()}
            onClick={() => handleSheetChange(Sheets.ConfirmSubmit, true)}
          >
            <SubmitButtonContent isSubmitted={isSubmitted(schema.sheetId)} />
          </Button>
        </Bottombar>
      )}

      <ConfirmActionSheet
        open={sheetState === Sheets.ConfirmSubmit}
        onClose={() => handleSheetChange(Sheets.ConfirmSubmit, false)}
        onClickBack={() => handleSheetChange(Sheets.ConfirmSubmit, false)}
        onClickConfirm={handleSubmit}
      />

      <ConfirmActionSheet
        open={deleteConfirmOpen}
        title="Delete SKU"
        description="Are you sure you want to delete this SKU?"
        onClose={cancelDelete}
        onClickBack={cancelDelete}
        onClickConfirm={confirmDelete}
      />

      <EndShiftSheet
        open={currSheet === RmPmSheets.EndShift}
        title="End Process"
        description="You are making the end of your process for today."
        pendingDescription="You are making the end of your process for today. Please select a reason below"
        onClickBack={closeSheets}
        onClickConfirm={handleEndProcess}
        onOpenChange={(open) => !open && closeSheets()}
      />

      <AddTruckSheet
        open={currSheet === RmPmSheets.AddTruck}
        onClose={closeSheets}
      />
      <AddTruckProductsSheet
        open={currSheet === RmPmSheets.AddProducts}
        sheetId={metaSchema?.sheetId || ""}
        truckType={truckType}
        truckNumber={truckNumber}
        pmType={pmType}
        onClose={closeSheets}
      />
      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}

interface SubmitButtonContentProps {
  isSubmitted?: boolean
}

function SubmitButtonContent({ isSubmitted }: SubmitButtonContentProps) {
  if (isSubmitted) {
    return (
      <span className="flex gap-2 items-center">
        <div>Submitted Successfully</div>
        <Icon name="CheckboxOutline" className="text-white h-6 w-auto" />
      </span>
    )
  }

  return "Submit Truck Details"
}
